import { useState, useEffect } from "react";

// Shared design tokens (kept in sync with ProductPage.js PT tokens).
const PT = {
  font: "'Plus Jakarta Sans', sans-serif",
  orange: "#FF7B1C",
  orangeBg: "#fff0e4",
  orangeLine: "#ffd9bd",
  orangeInk: "#8a3f0c",
};

const pad = (n) => String(n).padStart(2, "0");

const TIME_ZONE = "Africa/Lagos";

const zoneFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: TIME_ZONE,
  hour12: false,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

const getZonedParts = (date) => {
  const parts = {};
  zoneFormatter.formatToParts(date).forEach(({ type, value }) => {
    if (type !== "literal") parts[type] = Number(value);
  });
  if (parts.hour === 24) parts.hour = 0;
  return parts;
};

const civilDay = ({ year, month, day }) =>
  new Date(Date.UTC(year, month - 1, day));

const addDays = (date, days) => {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + days);
  return d;
};

const getCountdownState = (cutoffHour) => {
  const now = new Date();
  const parts = getZonedParts(now);

  const secondsIntoDay = parts.hour * 3600 + parts.minute * 60 + parts.second;
  const cutoffSeconds = Math.round(cutoffHour * 3600);
  const missedToday = secondsIntoDay >= cutoffSeconds;

  const totalSeconds = missedToday
    ? cutoffSeconds + 86400 - secondsIntoDay
    : cutoffSeconds - secondsIntoDay;

  const today = civilDay(parts);
  const cutoffDay = missedToday ? addDays(today, 1) : today;

  // Delivery is the day after whichever cutoff you make.
  let delivery = addDays(cutoffDay, 1);
  // No Sunday deliveries — roll a Sunday delivery forward to Monday.
  if (delivery.getUTCDay() === 0) delivery = addDays(delivery, 1);

  const dayDiff = Math.round((delivery - today) / 86400000);
  const label =
    dayDiff === 1
      ? "tomorrow"
      : delivery.toLocaleDateString("en-US", {
          weekday: "long",
          timeZone: "UTC",
        });

  return {
    h: Math.floor(totalSeconds / 3600),
    m: Math.floor((totalSeconds % 3600) / 60),
    s: totalSeconds % 60,
    label,
  };
};

const ClockIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.3"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const DeliveryCountdownBar = ({
  cutoffHour = 10,
 isMobile = false,
  deliveryLabel,
  style = {},
  className = "",
}) => {
  const [time, setTime] = useState(() => getCountdownState(cutoffHour));

  useEffect(() => {
    const id = setInterval(() => setTime(getCountdownState(cutoffHour)), 1000);
    return () => clearInterval(id);
  }, [cutoffHour]);

  return (
    <div
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "9px",
        padding: isMobile ? "4px" : "10px",
        background: PT.orangeBg,
        borderBottom: `1px solid ${PT.orangeLine}`,
        color: PT.orangeInk,
        fontFamily: PT.font,
        fontSize: "13px",
        fontWeight: 800,
        ...style,
      }}
    >
      <span style={{ color: PT.orange, display: "flex" }}>
        <ClockIcon />
      </span>
      <span>
        Get it <b style={{ color: PT.orange }}>{deliveryLabel ?? time.label}</b>{" "}
        — order within{" "}
        <b style={{ color: PT.orange }}>
          {pad(time.h)}:{pad(time.m)}:{pad(time.s)}
        </b>
      </span>
    </div>
  );
};

export default DeliveryCountdownBar;
